import type { ScrollableElementRef } from '@ps/isp-web-component-library';
import { ScrollableElement } from '@ps/isp-web-component-library';
import { notEmpty } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import { EventTypeId } from '@ps/web-sports-enums';
import { flatMap, sortBy, uniq } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketLevelCompoundMarketTemplateProps,
  MarketTemplateComponent
} from '@swf/sports/types/templates';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import { TXTWCL_Show_More } from './PlayerAlternatives.messages.json';
import styles from './PlayerAlternatives.scss';

const SHOW_MORE_THRESHOLD = 10;
const RUNNER_VALUE_PATTERN = /(\d+)\+/;
const STICKY_COLUMN_WIDTH = 136;

const PlayerAlternatives: MarketTemplateComponent<MarketLevelCompoundMarketTemplateProps> = ({
  marketIds,
  config
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const markets = marketIds
    .filter(notEmpty)
    .map((marketId) => allMarkets[marketId])
    .filter(notEmpty);
  const events = useSelector(widgetSelect.events);
  const [eventTypeId] = Object.keys(useSelector(widgetSelect.eventTypes));
  const isBasketball = Number(eventTypeId) === EventTypeId.BASKETBALL;

  const runnerValues = flatMap(markets.map((market) => market.runners)).map((runner) =>
    Number(runner.runnerName.match(RUNNER_VALUE_PATTERN)?.[1])
  );
  const marketValues = markets.map((market) =>
    Number(market.marketType.match(RUNNER_VALUE_PATTERN)?.[1])
  );
  const runnerValuesBasketball = flatMap(markets.map((market) => market.runners)).map(
    (runner) => runner.runnerName
  );

  const sortedRunnerValues = sortBy(uniq(runnerValues), (v) => Number(v));
  const sortedMarketValues = sortBy(uniq(marketValues), (v) => Number(v));
  const sortedRunnerValuesBasketball = uniq(runnerValuesBasketball);

  const sortedColumnValues = isBasketball ? sortedMarketValues : sortedRunnerValues;

  const sortedMarkets = sortBy(
    markets,
    (market) => market.runners[0].winRunnerOdds?.decimalDisplayOdds?.decimalOdds ?? 0
  );

  const [marketRowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    sortedMarkets,
    sortedMarkets[0]?.marketId,
    SHOW_MORE_THRESHOLD
  );

  const [runnerRowsToDisplay, handleShowMoreClickBasketball, limitRowsBasketball] =
    useMarketDisplayLimit(
      sortedRunnerValuesBasketball,
      sortedRunnerValuesBasketball[0],
      SHOW_MORE_THRESHOLD
    );

  const ref = useRef<ScrollableElementRef>(null);
  const tdRef = useRef<HTMLTableCellElement>(null);

  const initialScrollStartColumn =
    'initialScrollStartColumn' in config ? config.initialScrollStartColumn : 0;

  useEffect(() => {
    if (initialScrollStartColumn)
      ref.current?.scrollTo(
        (tdRef.current?.offsetLeft ?? STICKY_COLUMN_WIDTH) - STICKY_COLUMN_WIDTH
      );
  }, [initialScrollStartColumn]);

  return (
    <>
      <ScrollableElement ref={ref} buttonVerticalAlignment="top">
        <table className={styles.playerAlternatives}>
          <thead>
            <tr>
              <td className={styles.sticky}>&nbsp;</td>
              {sortedColumnValues.map((title, idx) => (
                <th
                  scope="col"
                  key={title}
                  ref={idx === initialScrollStartColumn ? tdRef : undefined}
                >
                  {title}+
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isBasketball
              ? runnerRowsToDisplay.map((valuePlayerName) => (
                  <tr key={valuePlayerName}>
                    <th scope="row" className={styles.sticky}>
                      <span>{valuePlayerName}</span>
                    </th>
                    {sortedColumnValues.map((sortedColumnValue) => {
                      const market = sortedMarkets.find(
                        (m) =>
                          m.marketType.match(RUNNER_VALUE_PATTERN)?.[1] ===
                          String(sortedColumnValue)
                      );
                      if (!market) {
                        return null;
                      }
                      const runner = market.runners.find((r) => r.runnerName === valuePlayerName);
                      return runner ? (
                        <td key={`${runner.selectionId}-${sortedColumnValue}`}>
                          <Selection
                            competitionId={market.competitionId}
                            eventId={market.eventId}
                            eventTypeId={market.eventTypeId}
                            handicap={runner.handicap}
                            legTypes={market.legTypes}
                            marketId={market.marketId}
                            marketStatus={market.marketStatus}
                            marketType={market.marketType}
                            odds={runner.winRunnerOdds}
                            runnerResultType={runner.resultType}
                            runnerStatus={runner.runnerStatus}
                            selectionId={runner.selectionId}
                            startTime={events[market.eventId].eventStartTime}
                          />
                        </td>
                      ) : (
                        <td key={`cell-${sortedColumnValue}`}>
                          <span className={styles.emptySelection}>-</span>
                        </td>
                      );
                    })}
                  </tr>
                ))
              : marketRowsToDisplay.map(
                  ({
                    marketId,
                    eventId,
                    legTypes,
                    marketName,
                    marketType,
                    marketStatus,
                    runners,
                    eventTypeId,
                    competitionId
                  }) => (
                    <tr key={marketName}>
                      <th scope="row" className={styles.sticky}>
                        <span>{marketName.split(' - ')[0]}</span>
                      </th>
                      {sortedColumnValues.map((sortedRunnerValue) => {
                        const runner = runners.find(
                          (r) =>
                            r.runnerName.match(RUNNER_VALUE_PATTERN)?.[1] ===
                            String(sortedRunnerValue)
                        );
                        return runner ? (
                          <td key={`${runner.selectionId}`}>
                            <Selection
                              competitionId={competitionId}
                              eventId={eventId}
                              eventTypeId={eventTypeId}
                              handicap={runner.handicap}
                              legTypes={legTypes}
                              marketId={marketId}
                              marketStatus={marketStatus}
                              marketType={marketType}
                              odds={runner.winRunnerOdds}
                              runnerResultType={runner.resultType}
                              runnerStatus={runner.runnerStatus}
                              selectionId={runner.selectionId}
                              startTime={events[eventId].eventStartTime}
                            />
                          </td>
                        ) : (
                          <td key={`cell-${sortedRunnerValue}`}>
                            <span className={styles.emptySelection}>-</span>
                          </td>
                        );
                      })}
                    </tr>
                  )
                )}
          </tbody>
        </table>
      </ScrollableElement>
      {(isBasketball ? limitRowsBasketball : limitRows) && (
        <div className={styles.showMoreWrapper}>
          <button
            type="button"
            className={styles.showMore}
            onClick={isBasketball ? handleShowMoreClickBasketball : handleShowMoreClick}
          >
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

PlayerAlternatives.marketTemplateType = 'EVENT';

export default PlayerAlternatives;
